<template>
  <div class="p-4 flex flex-col gap-1.5 h-67.5 glass-frosted w-full rounded-8xl">
    <p class="text-s-medium text-text-glass-default">{{ $t('pageAndPlayer.player.sortTip') }}</p>
    <TransitionGroup
      name="screen-sort"
      tag="div"
      class="flex gap-3 relative"
      @dragover.prevent
      @drop.prevent="handleDrop"
    >
      <div
        v-for="screen in sortedScreens"
        :key="screen.slot"
        :draggable="screen.kind === 'custom'"
        class="screen-sort-item relative w-38.5 h-24 rounded-lg overflow-hidden transition-opacity"
        :class="[
          screen.kind === 'custom' ? 'cursor-grab' : 'cursor-default',
          draggingSlot === screen.slot ? 'opacity-60 cursor-grabbing' : '',
        ]"
        @dragstart="(e) => handleDragStart(screen.slot, e)"
        @dragenter.prevent="() => handleDragEnter(screen.slot)"
        @dragover.prevent
        @drop.prevent="handleDrop"
        @dragend="handleDragEnd"
      >
        <img
          v-if="screen.thumbnail && !screen.error"
          :src="screen.thumbnail"
          class="w-38.5 h-24 object-cover"
        />
        <div
          v-else-if="screen.error"
          class="w-full h-full p-2 flex justify-center items-center gap-1 bg-white text-s-medium text-brand-blue1"
        >
          <Icon icon="Multimedia-Fill-Cut" class="size-4" />
          <span>{{ $t('pageAndPlayer.player.imgMissing') }}</span>
        </div>
        <div class="absolute inset-0 flex flex-col justify-between p-2 bg-mask20">
          <div class="flex justify-end">
            <div class="flex items-center justify-center rounded-full size-4 bg-brand-red">
              <span class="text-white text-m-medium font-[Barlow]">{{ screen.order }}</span>
            </div>
          </div>
          <div class="flex items-center justify-between w-full">
            <div class="p-1.5 inline-block glass-frosted text-s-medium text-white rounded-lg">
              <span v-if="screen.kind === 'home'">
                {{ $t('pageAndPlayer.pages.systemPage.home') }}
              </span>
              <span v-else-if="screen.kind === 'mouse-rt'">
                {{ $t('pageAndPlayer.pages.systemPage.mouseRt') }}
              </span>
              <span v-else-if="screen.kind === 'custom'">
                {{ screen.thumbname || screen.id }}{{ screen.thumbext }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </TransitionGroup>
  </div>
</template>

<script setup lang="ts">
  import { computed, ref } from 'vue'
  import { Icon } from '@rapoo/element'
  import { getDragData, setDragData, type BaseDragData } from '@/utils/dnd'
  import type { PageItem } from '../../utils/types'
  import { repositionPageItem } from '../../utils/pages'

  const DRAG_TYPE_SCREEN_SORT = 'application/x-rapoo-screen-sort'

  interface ScreenSortDragData extends BaseDragData {
    slot: number
  }

  const props = defineProps<{
    pages: PageItem[]
  }>()

  const emit = defineEmits<{
    (e: 'update:order', pages: PageItem[]): void
  }>()

  const draggingSlot = ref<number | null>(null)
  const dragOverSlot = ref<number | null>(null)
  const draggingScreens = ref<PageItem[] | null>(null)

  const sortedScreens = computed(() => {
    if (draggingScreens.value) return draggingScreens.value

    return props.pages
  })

  const moveScreen = (targetSlot: number) => {
    const screens = draggingScreens.value
    if (
      draggingSlot.value === null ||
      draggingSlot.value === targetSlot ||
      !screens ||
      screens.find((item) => item.slot === draggingSlot.value)?.kind !== 'custom' ||
      screens.find((item) => item.slot === targetSlot)?.kind !== 'custom'
    ) {
      return
    }

    const fromIndex = screens.findIndex((item) => item.slot === draggingSlot.value)
    const toIndex = screens.findIndex((item) => item.slot === targetSlot)

    if (fromIndex === -1 || toIndex === -1) return

    draggingScreens.value = repositionPageItem(screens, { index: fromIndex }, toIndex)
  }

  const getDraggingScreenSlot = (dataTransfer: DataTransfer | null) => {
    if (!dataTransfer) return null

    const data = getDragData<ScreenSortDragData>(dataTransfer, DRAG_TYPE_SCREEN_SORT)

    return data ? data.slot : null
  }

  const handleDragStart = (slot: number, e: DragEvent) => {
    if (sortedScreens.value.find((item) => item.slot === slot)?.kind !== 'custom') {
      e.preventDefault()
      return
    }

    draggingSlot.value = slot
    draggingScreens.value = [...sortedScreens.value]

    if (!e.dataTransfer) return

    setDragData(e.dataTransfer, DRAG_TYPE_SCREEN_SORT, { slot })
    e.dataTransfer.effectAllowed = 'move'
  }

  const handleDragEnter = (targetSlot: number) => {
    if (dragOverSlot.value === targetSlot) return

    dragOverSlot.value = targetSlot
    moveScreen(targetSlot)
  }

  const handleDrop = (e: DragEvent) => {
    if (getDraggingScreenSlot(e.dataTransfer) !== draggingSlot.value) return
    if (!draggingScreens.value) return

    emit('update:order', draggingScreens.value)
    handleDragEnd()
  }

  const handleDragEnd = () => {
    draggingSlot.value = null
    dragOverSlot.value = null
    draggingScreens.value = null
  }
</script>

<style scoped>
  .screen-sort-move {
    transition: transform 0.2s ease;
  }

  .screen-sort-item {
    transition:
      transform 0.2s ease,
      opacity 0.2s ease;
  }
</style>
